/**
 * The lesson coach (docs/HINTS-LAYOUT-TUTORIAL.md B7): dims everything but the
 * step's spot, and Tibbs says the step's line in a bubble that never covers
 * the spot. Taps pass through to the table, where only the taught move
 * responds. Adapted from components/table/Coach.tsx (copied, not imported).
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties } from 'react';
import { Tibbs } from '../components/Art';
import { IconClose } from '../components/Icons';
import { reducedMotion } from '../lib/motion';
import { spotSelector } from './script';
import type { Step } from './script';

interface Box {
  left: number;
  top: number;
  width: number;
  height: number;
}

const PAD = 8;
const GAP = 12;
const EDGE = 8;

function rectOf(selectors: string[], pad: number): Box | null {
  let l = Infinity;
  let t = Infinity;
  let r = -Infinity;
  let b = -Infinity;
  for (const sel of selectors) {
    document.querySelectorAll<HTMLElement>(sel).forEach((el) => {
      const rc = el.getBoundingClientRect();
      if (!rc.width || !rc.height) return;
      // A target that rocks or breathes changes its bounding box; use its untransformed size around its center.
      const w = el.offsetWidth || rc.width;
      const h = el.offsetHeight || rc.height;
      const cx = rc.left + rc.width / 2;
      const cy = rc.top + rc.height / 2;
      l = Math.min(l, cx - w / 2);
      t = Math.min(t, cy - h / 2);
      r = Math.max(r, cx + w / 2);
      b = Math.max(b, cy + h / 2);
    });
  }
  if (!Number.isFinite(l)) return null;
  return { left: l - pad, top: t - pad, width: r - l + pad * 2, height: b - t + pad * 2 };
}

const sameBox = (a: Box | null, b: Box | null) =>
  a === b || (!!a && !!b && Math.abs(a.left - b.left) < 0.5 && Math.abs(a.top - b.top) < 0.5 && Math.abs(a.width - b.width) < 0.5 && Math.abs(a.height - b.height) < 0.5);

interface Measured {
  /** The union of the spot elements: the bubble never overlaps it. */
  spot: Box | null;
  /** One lit hole per spot entry, so nothing between two spots looks taught. */
  holes: Box[];
  /** Where the bubble anchors when the step has no spot (Ada's tile), or the hole-end sheet on the end step. */
  anchor: Box | null;
  sheet: Box | null;
  barBottom: number;
  vw: number;
  vh: number;
}

function measure(step: Step): Measured {
  const sheetEl = document.querySelector<HTMLElement>('.holeend-sheet');
  const sheetRc = sheetEl?.getBoundingClientRect();
  const bar = document.querySelector<HTMLElement>('.topbar')?.getBoundingClientRect();
  return {
    spot: step.spot.length ? rectOf(step.spot.map((s) => spotSelector(s)), PAD) : null,
    holes: step.spot.flatMap((s) => rectOf([spotSelector(s)], PAD) ?? []),
    anchor: step.spot.length ? null : rectOf(['.tile[data-seat="1"]'], 4),
    sheet: sheetRc && sheetRc.height ? { left: sheetRc.left, top: sheetRc.top, width: sheetRc.width, height: sheetRc.height } : null,
    barBottom: bar ? bar.bottom : 50,
    vw: window.innerWidth,
    vh: window.innerHeight,
  };
}

const sameMeasure = (a: Measured, b: Measured) =>
  sameBox(a.spot, b.spot) && a.holes.length === b.holes.length && a.holes.every((h, i) => sameBox(h, b.holes[i] ?? null)) && sameBox(a.anchor, b.anchor) && sameBox(a.sheet, b.sheet) && a.barBottom === b.barBottom && a.vw === b.vw && a.vh === b.vh;

/** Bubble position (B7): below the spot when it sits high and there is room, else above, else under the top bar. Never over the spot. */
function place(m: Measured, step: Step, bh: number, bw: number): CSSProperties {
  const top0 = m.barBottom + EDGE;
  const left = (box: Box | null) => Math.max(GAP, Math.min(m.vw - GAP - bw, (box ? box.left + box.width / 2 : m.vw / 2) - bw / 2));
  if (step.kind === 'end' && m.sheet) {
    const t = m.sheet.top - GAP - bh;
    return { left: left(null), top: Math.max(top0, t) };
  }
  const box = m.spot ?? m.anchor;
  if (!box) return { left: left(null), top: top0 };
  const belowTop = box.top + box.height + GAP;
  const roomBelow = m.vh - EDGE - belowTop;
  const aboveTop = box.top - GAP - bh;
  const high = box.top < m.vh * 0.45;
  if (high && roomBelow >= Math.max(bh, 120)) return { left: left(box), top: belowTop };
  if (aboveTop >= top0) return { left: left(box), top: aboveTop };
  if (roomBelow >= bh) return { left: left(box), top: belowTop };
  return { left: left(box), top: top0 };
}

export interface CoachProps {
  stepIndex: number;
  step: Step;
  line: string;
  /** Bumps on every wrong tap. */
  nudge: number;
  /** Also pulse the spot on this nudge (do steps). */
  pulse: boolean;
  onNext: () => void;
  onExit: () => void;
  onSheet: () => void;
}

export function TutorialCoach({ stepIndex, step, line, nudge, pulse, onNext, onExit, onSheet }: CoachProps) {
  const [m, setM] = useState<Measured>(() => measure(step));
  // Let the deal land before the first spot is drawn (the cards fly in for about 0.85s).
  const [dealt, setDealt] = useState(() => reducedMotion());
  useEffect(() => {
    if (dealt) return;
    const t = setTimeout(() => setDealt(true), 900);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const bubble = useRef<HTMLDivElement>(null);
  const [bh, setBh] = useState(76);
  const onSheetRef = useRef(onSheet);
  onSheetRef.current = onSheet;

  // Old bubble out (150ms), then the new one rises in (250ms).
  const [shown, setShown] = useState({ stepIndex, step, line });
  const [leaving, setLeaving] = useState(false);
  useEffect(() => {
    if (stepIndex === shown.stepIndex) {
      if (line !== shown.line) setShown({ stepIndex, step, line });
      return;
    }
    if (reducedMotion()) {
      setShown({ stepIndex, step, line });
      return;
    }
    setLeaving(true);
    const t = setTimeout(() => {
      setShown({ stepIndex, step, line });
      setLeaving(false);
    }, 150);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepIndex, line]);

  // Re-measure every 400ms and on resize; report the hole-end sheet when it opens.
  useLayoutEffect(() => {
    let raf = 0;
    const update = () => {
      const next = measure(step);
      setM((cur) => (sameMeasure(cur, next) ? cur : next));
      if (next.sheet) onSheetRef.current();
    };
    update();
    // Every frame for the first moments of a step (cards still flying or dealing), then every 400ms.
    const t0 = performance.now();
    const follow = () => {
      update();
      if (performance.now() - t0 < 1200) raf = requestAnimationFrame(follow);
    };
    raf = requestAnimationFrame(follow);
    const iv = setInterval(update, 400);
    const on = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(update);
    };
    window.addEventListener('resize', on);
    return () => {
      clearInterval(iv);
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', on);
    };
  }, [step]);

  useLayoutEffect(() => {
    const h = bubble.current?.offsetHeight;
    if (h && Math.abs(h - bh) > 0.5) setBh(h);
  });

  // Wrong tap: pulse the spot and nudge the bubble for 300ms.
  const [hit, setHit] = useState<{ n: number; pulse: boolean } | null>(null);
  useEffect(() => {
    if (!nudge || reducedMotion()) return;
    setHit({ n: nudge, pulse });
    const t = setTimeout(() => setHit((h) => (h?.n === nudge ? null : h)), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nudge]);

  // Enter advances a tap step.
  const tapStep = shown.step.kind === 'tap';
  const onNextRef = useRef(onNext);
  onNextRef.current = onNext;
  useEffect(() => {
    if (!tapStep) return;
    const on = (e: KeyboardEvent) => {
      if (e.key !== 'Enter' || e.metaKey || e.ctrlKey || e.altKey) return;
      e.preventDefault();
      e.stopPropagation();
      onNextRef.current();
    };
    window.addEventListener('keydown', on, true);
    return () => window.removeEventListener('keydown', on, true);
  }, [tapStep]);

  const bw = Math.min(340, m.vw - 24);
  const placed = place(m, shown.step, bh, bw);
  // A leaving bubble stays where it was while it fades.
  const lastPos = useRef(placed);
  if (!leaving) lastPos.current = placed;
  const pos = leaving ? lastPos.current : placed;
  const sayStep = shown.step.kind === 'say' && !shown.step.untilSheet;
  if (!dealt) return null;
  return (
    <div className="tut-coach">
      {m.holes.length > 0 && (
        <svg className={`tut-dim${hit?.pulse ? ' pulse' : ''}`} width={m.vw} height={m.vh} aria-hidden="true">
          <defs>
            <mask id="tut-holes" maskUnits="userSpaceOnUse" x="0" y="0" width={m.vw} height={m.vh}>
              <rect width={m.vw} height={m.vh} fill="#fff" />
              {m.holes.map((h, i) => (
                <rect key={i} className="tut-hole" x={h.left} y={h.top} width={h.width} height={h.height} rx="16" fill="#000" />
              ))}
            </mask>
          </defs>
          <rect className="tut-shade" width={m.vw} height={m.vh} mask="url(#tut-holes)" />
          {m.holes.map((h, i) => (
            <rect key={i} className="tut-hole tut-ring" x={h.left} y={h.top} width={h.width} height={h.height} rx="16" />
          ))}
        </svg>
      )}
      <div
        ref={bubble}
        key={shown.stepIndex}
        className={`tut-bubble${leaving ? ' leaving' : ''}${hit ? ' nudge' : ''}${sayStep ? ' tappable' : ''}`}
        style={{ ...pos, width: bw }}
        role="dialog"
        aria-live="polite"
        aria-label="Lesson"
        onClick={sayStep ? onNext : undefined}
      >
        <Tibbs pose="point" size={56} className="tut-tibbs" />
        <div className="tut-body">
          <p className="tut-line">{shown.line}</p>
          {tapStep && (
            <button
              type="button"
              className="btn btn-ink btn-sm tut-next"
              onClick={(e) => {
                e.stopPropagation();
                onNext();
              }}
              autoFocus
            >
              Next
            </button>
          )}
        </div>
        <button
          type="button"
          className="icon-btn tut-x"
          aria-label="Leave the tutorial"
          onClick={(e) => {
            e.stopPropagation();
            onExit();
          }}
        >
          <IconClose size={18} />
        </button>
      </div>
    </div>
  );
}
